import Storage from "./storage";
import Application from "./application";
import Api from "./api";
import { compressImage, getShortName, isBase64String, sortBy } from "../utils";
import IUserPermissions from "../interface/user-permissions";
import IUserDataRaw from "../interface/user-data-raw";
import IUserData from "../interface/user-data";
import IUserApplication from "../interface/user-application";
import IStorageLoginData from "../interface/storage-login-data";
import IImageResponse from "../interface/image-response";
import IApiUserLoaded from "../interface/api/api-user-loaded";
import IApiLoginData from "../interface/api/api-login-data";
import Global from "../global";
import StorageItem from "../enum/storage-item";
import Permission from "../enum/permission";
import ImageResponseType from "../enum/image-response-type";
import ErrorCode from "../enum/error-code";

class UserInstance {

    public Applications: IUserApplication[] = [];

    private UserName: string = '';

    private Image: string | undefined;

    private isUserAuthorized: boolean = false;

    private DisabledModules: number[] = [];

    private DisabledTables: number[] = [];

    private AccessLevel: number | undefined;

    private LoadedUser: IApiUserLoaded | null = null;

    private Instance: string = '';

    /* Public Methods */

    public getName(): string {
        return this.UserName;
    }

    public getApplications(): IUserApplication[] {
        const currentId: string = Application.getId();
        this.Applications.forEach((app: IUserApplication) => {
            app.isCurrent = app.id === currentId;
        });
        return this.Applications;
    }

    public getOtherApplications(): IUserApplication[] {
        const appId: string = Application.getId();
        return this.getApplications().filter((app: IUserApplication) => app.id !== appId);
    }

    public async changePassword(password: string, old: string): Promise<boolean> {
        password = password.trim();
        // TODO - Add old password comparison
        if (password.length) {
            const user: IApiUserLoaded = await this.load();
            user.password = password;
            await Api.userSave(user);
            const latestLogin: IStorageLoginData | null = Storage.getItem(StorageItem.LatestLogin);
            if (latestLogin) {
                latestLogin.password = password;
                Storage.setItem(StorageItem.LatestLogin, latestLogin);
            }
            return true;
        } else {
            throw ErrorCode.PasswordInvalid;
        }
    }

    public async setCallUrl(callUrl: string): Promise<string> {
        const user: IApiUserLoaded = await this.load();
        user.callUrl = callUrl.trim();
        await Api.userSave(user);
        return user.callUrl;
    }

    public async getCallUrl(): Promise<string> {
        const user: IApiUserLoaded = await this.load();
        return user.callUrl;
    }

    public hasTableAccess(entityId: number): boolean {
        return !this.DisabledTables.includes(entityId);
    }

    public hasModuleAccess(moduleId: number): boolean {
        return !this.DisabledModules.includes(moduleId);
    }

    public isAdmin(): boolean {
        return this.access(Permission.Admin);
    }

    public isDesigner(): boolean {
        return this.access(Permission.Designer);
    }

    public isDelete(): boolean {
        return this.access(Permission.Delete);
    }

    public isWrite(): boolean {
        return this.access(Permission.Write);
    }

    public getPermissions(): IUserPermissions {
        return {
            Admin: this.isAdmin(),
            Designer: this.isDesigner(),
            Delete: this.isDelete(),
            Write: this.isWrite()
        };
    }

    public getImage(): IImageResponse {
        if (this.Image) {
            return {
                type: ImageResponseType.Image,
                value: this.Image
            };
        } else {
            return {
                type: ImageResponseType.ShortName,
                value: getShortName(this.getName())
            };
        }
    }

    public async setData(data: IUserDataRaw): Promise<IUserData> {
        const user: IApiUserLoaded = await this.load();
        if (!data.image || isBase64String(data.image)) {
            if (data.image) {
                data.image = await compressImage(data.image, Global.AVATAR_COMPRESS_SCALE);
            }
            user.name = data.name || user.name;
            user.name = user.name.trim();
            user.logo = data.image === undefined ? user.logo : data.image;
            await Api.userSave(user);
            this.UserName = user.name;
            this.Image = user.logo;
            const latestLogin: IStorageLoginData | null = Storage.getItem(StorageItem.LatestLogin);
            if (latestLogin) {
                latestLogin.user = user.name;
                Storage.setItem(StorageItem.LatestLogin, latestLogin);
            }
            Storage.setLatestApplication(this.UserName.toString(), Application.getId());
            return this.getData();
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Public Methods */

    public getData(): IUserData {
        return {
            UserName: this.getName(),
            UserImage: this.getImage(),
            Permissions: this.getPermissions()
        }
    }

    public setUser(user: string, instanceName: string, applications: IUserApplication[]): void {
        this.UserName = user;
        this.isUserAuthorized = true;
        this.Applications = sortBy(applications, 'name');
        this.Instance = instanceName;
    }

    public rememberUser(user: string, password: string, instanceName: string): void {
        if (!this.isPublicUser()) {
            Storage.setItem(StorageItem.LatestLogin, { user, password, instanceName });
            Storage.setItem(StorageItem.LatestInstance, instanceName);
        }
    }

    public getApplication(appId: string): IUserApplication | null {
        return this.Applications.find((app: IUserApplication) => app.id === appId) || null;
    }

    public setApplication(applicationId: string, loginData: IApiLoginData): void {
        if (this.UserName) {
            this.Image = Api.getUserLogo();
            Storage.setLatestApplication(this.UserName.toString(), applicationId);
            this.DisabledModules = loginData.jSonLogin.disabledModules || [];
            this.DisabledTables = loginData.jSonLogin.disabledTables || [];
            this.AccessLevel = loginData.accessLevel;
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public isRestricted(): boolean {
        return this.access(Permission.Restricted);
    }

    public isAuthorized(): boolean {
        return this.isUserAuthorized;
    }

    public logout(): void {
        this.UserName = '';
        this.LoadedUser = null;
        this.isUserAuthorized = false;
        this.AccessLevel = undefined;
        this.DisabledModules = [];
        this.DisabledTables = [];
        this.Applications = [];
        Storage.setItem(StorageItem.LatestLogin, false);
    }

    public async deleteSelf(): Promise<void> {
        if (this.isAuthorized()) {
            const user: IApiUserLoaded = await this.load();
            await user.delete();
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public hasDesignerAccess(): boolean {
        if (this.isDesigner()) {
            return true;
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public getInstance(): string {
        return this.Instance;
    }

    /* Private Methods */

    private isPublicUser(): boolean {
        return this.getName() === Application.getPublicUserName();
    }

    private async load(): Promise<IApiUserLoaded> {
        if (!this.LoadedUser) {
            const user: IApiUserLoaded | null = await Api.userLoad(this.getName());
            if (user) {
                this.LoadedUser = user;
            } else {
                throw ErrorCode.ApiInternal;
            }
        }
        return this.LoadedUser;
    }

    private access(permission: Permission): boolean {
        if (this.AccessLevel) {
            return (this.AccessLevel & permission) === permission;
        }
        return false;
    }

}

const User = new UserInstance();

export default User;